<template>
  <div v-if="$can('view', 'Yacht')">
    <!--<editor-fold desc="Logistics plan">-->
    <v-row
      v-if="yacht.delivery_plan && yacht.delivery_plan.saved"
      align="center"
      class="subtitle-1 pa-4"
    >
      <v-col cols="8">
        <div>Logistics Plan</div>
        <div class="grey--text body-2">
          <span v-if="yacht.delivery_plan_created_at">
            Created
            <span v-if="yacht.delivery_plan_created_by_user">by <strong>{{ yacht.delivery_plan_created_by_user.staff.name }}</strong></span>
            on <strong>{{ yacht.delivery_plan_created_at | moment('D MMM YYYY HH:mm:ss') }}</strong> &middot;
          </span>
          <span v-if="yacht.delivery_plan_created_at !== yacht.delivery_plan_updated_at">
            Updated
            <span v-if="yacht.delivery_plan_updated_by_user">by <strong>{{ yacht.delivery_plan_updated_by_user.staff.name }}</strong></span>
            on <strong>{{ yacht.delivery_plan_updated_at | moment('D MMM YYYY HH:mm:ss') }}</strong>
          </span>
        </div>
      </v-col>
      <v-col
        cols="8"
        class="text-right"
      >
        <v-btn
          color="secondary"
          outlined
          @click="$emit('edit:delivery-plan')"
        >
          <v-icon
            left
            small
          >
            edit
          </v-icon>
          Update Plan
        </v-btn>
      </v-col>
    </v-row>
    <v-card
      v-if="yacht.delivery_plan && yacht.delivery_plan.saved"
      class="elevation-10 radius-10 mb-12"
    >
      <v-card-text class="px-8 py-10">
        <v-row
          justify="center"
          no-gutters
        >
          <v-col>
            <v-timeline
              align-top
              dense
            >
              <v-timeline-item
                v-for="(step, i) in yacht.delivery_plan.steps"
                :key="step.id"
                :color="step.ended ? 'success' : 'grey'"
              >
                <template v-slot:icon>
                  <span class="white--text">
                    {{ i + 1 }}
                  </span>
                </template>
                <v-row
                  class="pt-2"
                  no-gutters
                >
                  <v-col
                    cols="3"
                    class="grey--text text--darken-2"
                  >
                    {{ step.estimated_at | moment('D MMM YYYY') }}
                  </v-col>
                  <v-col>
                    <div class="font-weight-bold">
                      {{ step.title }}
                    </div>
                    <div class="caption">
                      <flag
                        class="mr-1"
                        :iso="step.processor ? step.processor.country.code : yacht.country.code"
                        :squared="false"
                      />
                      {{ step.processor && step.processor.country ? step.processor.country.name : yacht.country.name }}
                      <span v-if="step.processor && (step.processor.name || step.processor.label)">
                        &middot; <strong>{{ step.processor.name || step.processor.label }}</strong>
                      </span>
                    </div>
                    <div
                      v-if="!step.started && !step.ended && (!yacht.delivery_plan.steps[i - 1] || yacht.delivery_plan.steps[i-1].ended)"
                      class="mt-4"
                    >
                      <v-btn
                        color="primary ma-0"
                        @click="$emit('edit:delivery-plan', step.id)"
                      >
                        <v-icon
                          left
                          small
                        >
                          check
                        </v-icon>
                        Mark as done
                      </v-btn>
                    </div>
                    <div
                      v-if="step.started && !step.ended"
                      class="mt-4"
                      @click="$emit('edit:delivery-plan', step.id)"
                    >
                      <v-btn color="primary ma-0">
                        <v-icon
                          left
                          small
                        >
                          check
                        </v-icon>
                        Mark as ended
                      </v-btn>
                    </div>
                    <div
                      v-if="step.type === 'equipment' && step.metadata.equipment_packs"
                      class="mt-4"
                    >
                      <div class="pa-4 elevation-5 radius-10">
                        <equipment-pack-installed-row
                          v-for="equipmentPackId in step.metadata.equipment_packs"
                          :key="equipmentPackId"
                          :equipment-pack-id="equipmentPackId"
                          :all-equipment-packs.sync="yacht.equipment_packs"
                          :yacht="yacht"
                        />
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-timeline-item>
            </v-timeline>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <!--</editor-fold>-->

    <div
      v-else-if="(yacht.status === yachtStatuses.STATUS_PENDING || yacht.status === yachtStatuses.STATUS_IN_USE) && canSetDeliveryPlan"
      class="text-center pa-12"
    >
      <div
        class="grey--text text--darken-2 subtitle-1 mb-4"
      >
        No logistics plan specified.
      </div>
      <!--      <div-->
      <!--        v-else-->
      <!--        class="grey&#45;&#45;text text&#45;&#45;darken-2 subtitle-1 mb-4"-->
      <!--      >-->
      <!--        Please set the commissioning yards below first.-->
      <!--      </div>-->
      <v-btn
        large
        color="primary"
        :disabled="!canSetDeliveryPlan"
        @click="$emit('edit:delivery-plan')"
      >
        <v-icon
          left
          small
        >
          format_list_numbered
        </v-icon>
        Set Logistics Plan
      </v-btn>
    </div>

    <div
      v-else-if="yacht.status !== yachtStatuses.STATUS_PENDING"
      class="text-center pa-12"
    >
      <div class="grey--text text--darken-2 subtitle-1 mb-4">
        Logistics plan cannot be specified until the yacht status is switched to "Pending Plan".
      </div>
      <v-btn
        large
        color="primary"
        @click="$emit('update:status', yachtStatuses.STATUS_PENDING)"
      >
        <v-icon
          left
          small
        >
          arrow_forward
        </v-icon>
        Switch to Pending Plan
      </v-btn>
    </div>

    <div
      v-if="yacht.status === yachtStatuses.STATUS_PENDING"
      class="subtitle-1 pa-4"
    >
      Set commissioning yards for equipment
    </div>
    <v-card
      v-if="yacht.status === yachtStatuses.STATUS_PENDING"
      class="elevation-10 radius-10 mb-12"
    >
      <v-card-text class="py-0 px-6">
        <v-row
          no-gutters
          align="center"
          class="list--bordered py-3"
        >
          <v-col
            cols="10"
            class="font-weight-bold"
          >
            Set commissioning yard for all equipment packs
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              v-model="allContractorsId"
              label="Select for all"
              outlined
              hide-details
              clearable
              :items="contractors.data"
              item-text="name"
              item-value="id"
              :loading="contractors.loading"
            />
          </v-col>
        </v-row>
        <v-row
          v-for="equipmentPack in commissioningEquipmentPacks"
          :key="equipmentPack.id"
          no-gutters
          align="center"
          class="list--bordered py-3"
        >
          <v-col
            cols="10"
          >
            <a
              href="#"
              @click.stop="showEquipmentPackItems(equipmentPack)"
            >{{ equipmentPack.label }}</a>
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              v-model="equipmentPack.contractor_id"
              v-validate="'required'"
              label="Select commissioning yard"
              outlined
              hide-details
              clearable
              :items="contractors.data"
              item-text="name"
              item-value="id"
              :data-vv-name="'contractor_'+equipmentPack.id"
              :error-messages="veeErrors.collect('contractor_'+equipmentPack.id)"
              :loading="contractors.loading"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions class="py-3 px-6">
        <v-spacer />
        <v-btn
          large
          color="primary"
          :loading="submittingYards"
          @click="saveCommissioningYards"
        >
          <v-icon
            left
            small
          >
            check
          </v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>

    <div class="subtitle-1 pa-4">
      Equipment Logistics
    </div>
    <v-card
      class="elevation-10 radius-10 mb-12"
    >
      <v-card-text class="pa-0">
        <v-simple-table>
          <thead class="v-data-table-header">
            <tr>
              <th class="text-left">
                Label
              </th>
              <th class="text-left">
                Inst. By
              </th>
              <th
                v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                class="text-left"
              >
                Price
              </th>
              <th
                v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
                class="text-left"
              >
                Purchase Price
              </th>
              <th class="text-left">
                Supplier
              </th>
              <th class="text-center">
                Ordered
              </th>
              <th class="text-center">
                Installed
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="pack in allEquipmentPacks"
              :key="pack.id"
            >
              <td class="font-weight-bold">
                <a
                  href="#"
                  @click.stop="showEquipmentPackItems(pack)"
                >
                  {{ pack.label }}
                </a>
              </td>
              <td>
                {{ installedByData[pack.installed_by].label }}
              </td>
              <td
                v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                class="text-no-wrap"
              >
                {{ pack.pivot_sale_price | currency(pack.currency) }}
              </td>
              <td
                v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
                class="text-no-wrap"
              >
                {{ pack.pivot_purchase_price | currency(pack.currency) }}
              </td>
              <td>
                <span v-if="pack.supplier">
                  {{ pack.supplier.name }}
                </span>
                <span
                  v-else
                  class="grey--text"
                >
                  N/A
                </span>
              </td>
              <td class="text-center">
                <v-btn
                  :text="!pack.ordered"
                  small
                  rounded
                  :color="!pack.ordered ? 'grey' : 'success'"
                  :disabled="!$can('set-eqpack-ordered-status', 'Yacht')"
                  class="text-capitalize"
                  @click="toggleEquipmentPackOrdered(pack.id, !pack.ordered)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ !pack.ordered ? 'close' : 'check' }}
                  </v-icon>
                  {{ !pack.ordered ? 'Not Ordered' : 'Ordered' }}
                </v-btn>
              </td>
              <td class="text-center">
                <v-btn
                  :text="!pack.installed"
                  small
                  :color="!pack.installed ? 'grey' : 'success'"
                  rounded
                  :disabled="!$can('set-eqpack-installed-status', 'Yacht')"
                  class="text-capitalize"
                  @click="toggleEquipmentPackInstalled(pack.id, !pack.installed)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ !pack.installed ? 'close' : 'check' }}
                  </v-icon>
                  {{ !pack.installed ? 'Not Installed' : 'Installed' }}
                </v-btn>
              </td>
              <td>
                <v-btn
                  text
                  color="grey darken-1"
                  small
                  rounded
                  class="text-none"
                  @click="$emit('document:preview', documentTemplate.TYPE_ORDER, null, pack.id)"
                >
                  <v-icon
                    left
                    small
                  >
                    fa fa-clipboard-list
                  </v-icon>
                  Order
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <yacht-equipment-items-dialog
      v-if="equipmentPackItemsDialog.currentPack"
      :pack="equipmentPackItemsDialog.currentPack"
      :visible.sync="equipmentPackItemsDialog.visible"
    />
  </div>
</template>

<script>
import Yacht from '@/models/Yacht'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import Contractor from '@/models/Contractor'
import EquipmentPackInstalledRow from '@/components/business/yachts/delivery/EquipmentPackInstalledRow'
import { cloneDeep } from 'lodash'
import DocumentTemplate from '@/models/DocumentTemplate'
import YachtEquipmentItemsDialog from '@/components/business/yachts/YachtEquipmentItemsDialog'

export default {
    name: 'Delivery',
    components: { YachtEquipmentItemsDialog, EquipmentPackInstalledRow },
    props: {
      yacht: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        yachtStatuses: Yacht,
        currentStep: null,
        allContractorsId: null,
        commissioningEquipmentPacks: [],
        allEquipmentPacks: [],
        contractors: {
          data: [],
          loading: false
        },
        submitting: false,
        submittingYards: false,
        equipmentPackItemsDialog: {
          currentPack: undefined,
          visible: false
        }
      }
    },
    computed: {
      canSetDeliveryPlan() {
        if (this.yacht.status === Yacht.STATUS_IN_USE) return true

        for (let pack of this.yacht.equipment_packs.filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_CONTRACTOR)) {
          if (!pack.contractor_id) return false
        }

        return true
      }
    },
    watch: {
      allContractorsId: function (newId) {
        this.commissioningEquipmentPacks.map(pack => {
          pack.contractor_id = newId
        })
      },
      yacht: function () {
        this.setPacks()
      }
    },
    created() {
      this.installedByData = YachtEquipmentPack.installedByData
      this.documentTemplate = DocumentTemplate

      this.fetchContractors()

      this.setPacks()
    },
    methods: {
      setPacks() {
        this.commissioningEquipmentPacks = cloneDeep(this.yacht.equipment_packs).filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_CONTRACTOR)
        this.allEquipmentPacks = cloneDeep(this.yacht.equipment_packs)
      },
      async fetchContractors() {
        this.contractors.loading = true

        try {
          this.contractors.data = await Contractor.api.fetch({ type: Contractor.TYPE_YARD })
        } catch (e) {
          throw e
        } finally {
          this.contractors.loading = false
        }
      },
      async saveCommissioningYards() {
        let validation = await this.$validator.validateAll()

        if (!validation) return

        this.submittingYards = true

        try {
          let data = this.commissioningEquipmentPacks.map(ep => {
            return {
              equipment_pack_id: ep.id,
              contractor_id: ep.contractor_id
            }
          })

          let response = await Yacht.api.storeEquipmentPackContractors(this.yacht.id, {
            equipment_packs: data
          })

          this.$emit('update:yacht', response.yacht)

          this.$vuetify.goTo(0)

          this.$store.dispatch('alert', {
            text: 'Commissioning yards for equipment packs saved',
            color: 'success'
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        } finally {
          this.submittingYards = false
        }
      },
      async toggleEquipmentPackOrdered(id, ordered) {
        let response = await Yacht.api.toggleEquipmentPackOrdered(this.yacht.id, id, ordered)

        if (!response.success) return

        this.allEquipmentPacks = response.yacht.equipment_packs
        this.commissioningEquipmentPacks = response.yacht.equipment_packs.filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_CONTRACTOR)
      },
      async toggleEquipmentPackInstalled(id, installed) {
        let response = await Yacht.api.toggleEquipmentPackInstalled(this.yacht.id, id, installed)

        if (!response.success) return

        this.allEquipmentPacks = response.yacht.equipment_packs
        this.commissioningEquipmentPacks = response.yacht.equipment_packs.filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_CONTRACTOR)
      },
      showEquipmentPackItems(equipmentPack) {
        this.equipmentPackItemsDialog.currentPack = equipmentPack
        this.equipmentPackItemsDialog.visible = true
      }
    }
  }
</script>
